<template>
  <v-row>
    <v-col xs="12" md="12" lg="12" class="pa-5">
      <v-alert v-model="alert" type="error" variant="tonal" border="bottom">
        {{ $t("home.charts.alert") }}
      </v-alert>
    </v-col>
    <v-row class="pa-2">
      <!---------------------------------------TOTAL ACTIVITY---------------------------------------->
      <v-col class="pr-2 pl-4" xs="12" md="6" lg="6" sm="6">
        <v-card v-if="!noActivity" flat>
          <v-card-title>{{ $t("home.charts.total_activity") }}</v-card-title>
          <v-row justify-center>
            <v-progress-circular
              v-if="totalActivity.loading"
              :size="50"
              class="mb-5"
              color="red"
              indeterminate
            />
          </v-row>
          <chart
            v-if="!totalActivity.loading"
            :type="totalActivity.type"
            :data="totalActivity.data"
          />
        </v-card>
      </v-col>
      <!--------------------------------------PAST 24H ACTIVITY-------------------------------------->
      <v-col class="pr-4 pl-2" xs="12" md="6" lg="6" sm="6">
        <v-card v-if="!noActivity" flat>
          <v-card-title>{{ $t("home.charts.past_24h_activity") }}</v-card-title>
          <v-row justify-center>
            <v-progress-circular
              v-if="past24hActivity.loading"
              :size="50"
              class="mb-5"
              color="red"
              indeterminate
            />
          </v-row>
          <chart
            v-if="!past24hActivity.loading"
            :type="past24hActivity.type"
            :data="past24hActivity.data"
          />
        </v-card>
      </v-col>
    </v-row>
    <v-col cols="12">
      <v-col class="pa-4 pt-3 pb-12" xs="12" md="12" lg="12" sm="12">
        <v-card v-if="!noActivity" flat>
          <v-card-title class="ml-6">{{
            $t("home.charts.past_activity")
          }}</v-card-title>
          <v-row :class="$vuetify.display.xs ? '' : 'pl-4'">
            <!-- Mobile: compact date picker in a menu -->
            <v-col v-if="$vuetify.display.xs" cols="12" class="pa-2 pb-0">
              <v-row align="center" class="px-1" no-gutters>
                <v-col class="pr-1">
                  <v-select
                    v-model="pastActivity.timePeriod"
                    :items="timePeriodItems"
                    :label="$t('home.charts.time_period')"
                    variant="outlined"
                    density="compact"
                    hide-details
                    item-title="title"
                    item-value="value"
                    color="red-darken-2"
                  />
                </v-col>
                <v-col class="px-1">
                  <v-menu v-model="mobileDateMenu" :close-on-content-click="false">
                    <template #activator="{ props }">
                      <v-text-field
                        v-bind="props"
                        :model-value="formattedDate"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        hide-details
                        variant="outlined"
                        density="compact"
                      />
                    </template>
                    <v-date-picker
                      v-model="pastActivity.time"
                      :type="pastActivity.timePeriod === 'Month' ? 'month' : 'date'"
                      color="red-darken-2"
                      reactive
                      @update:model-value="mobileDateMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="auto">
                  <v-btn icon variant="text" @click="getPastUserActivity(userId)">
                    <v-icon color="grey-darken-2">mdi-orbit-variant</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>

            <!-- Desktop: left column with full date picker -->
            <v-col v-if="!$vuetify.display.xs" class="pa-4 pl-12" md="4">
              <v-row>
                <v-select
                  v-model="pastActivity.timePeriod"
                  :items="timePeriodItems"
                  :label="$t('home.charts.time_period')"
                  :hint="$t('home.charts.select_time_period')"
                  variant="solo"
                  item-title="title"
                  item-value="value"
                  persistent-hint
                  color="red-darken-2"
                  background-color="grey-lighten-4"
                />
                <v-btn icon @click="getPastUserActivity(userId)">
                  <v-icon color="grey-darken-2">mdi-orbit-variant</v-icon>
                </v-btn>
              </v-row>
              <v-row justify-center>
                <v-date-picker
                  v-model="pastActivity.time"
                  :type="pastActivity.timePeriod === 'Month' ? 'month' : 'date'"
                  color="red-darken-2"
                  reactive
                />
              </v-row>
            </v-col>

            <!-- Chart column -->
            <v-col cols="12" :md="$vuetify.display.xs ? 12 : 8" style="min-height:280px">
              <v-row justify-center>
                <v-progress-circular
                  v-if="pastActivity.loading"
                  :size="70"
                  color="red"
                  indeterminate
                />
              </v-row>
              <div style="position:relative;width:100%;min-height:260px">
                <chart
                  v-if="!pastActivity.loading"
                  class="pr-3"
                  :type="pastActivity.type"
                  :data="pastActivity.data"
                />
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-col>
    <!----------------------------------------PAST ACTIVITY---------------------------------------->
  </v-row>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import Chart from "../chart.vue";

function generateRandomNumbers() {
  const arr = [];
  for (let a = 0; a < 3; a += 1) {
    arr.push(Math.floor(Math.random() * 256));
  }
  return arr;
}

export default {
  components: { chart: Chart },
  data: () => ({
    totalActivity: {
      loading: false,
      type: "",
      data: {},
    },
    past24hActivity: {
      loading: false,
      type: "",
      data: {},
    },
    pastActivity: {
      loading: false,
      type: "",
      data: {},
      timePeriod: "Day",
      time: new Date().toISOString().substr(0, 10),
    },
    timePeriodItems: [
      { title: "Month", value: "Month" },
      { title: "Week", value: "Week" },
      { title: "Day", value: "Day" },
    ],
    statusString: "",
    noActivity: true,
    alert: false,
    userId: null,
    mobileDateMenu: false,
  }),
  computed: {
    formattedDate() {
      if (!this.pastActivity.time) return "";
      let date;
      if (this.pastActivity.time instanceof Date) {
        date = this.pastActivity.time;
      } else {
        const str = this.pastActivity.time;
        date = new Date(
          this.pastActivity.timePeriod === "Month" ? str + "-01" : str,
        );
      }
      if (isNaN(date.getTime())) return "";
      return this.pastActivity.timePeriod === "Month"
        ? date.toLocaleDateString(undefined, { month: "short", year: "numeric" })
        : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    },
  },
  watch: {
    "pastActivity.time"(value) {
      if (value instanceof Date) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, "0");
        if (this.pastActivity.timePeriod === "Month") {
          this.pastActivity.time = `${y}-${m}`;
        } else {
          this.pastActivity.time = value.toISOString().split("T")[0];
        }
      }
    },
  },
  mounted() {
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.timePeriodItems[0].title = this.$t(
        "home.charts.time_period_items.month",
      );
      this.timePeriodItems[1].title = this.$t(
        "home.charts.time_period_items.week",
      );
      this.timePeriodItems[2].title = this.$t(
        "home.charts.time_period_items.day",
      );
    },
    loadCharts(statusString, userId) {
      this.statusString = statusString;
      this.userId = userId;
      this.noActivity = false;
      this.getPast24hUserActivity(userId);
      this.getPastUserActivity(userId);
      this.getTotalUserActivity(userId);
    },
    getTotalUserActivity(userId) {
      this.totalActivity.loading = true;
      getRequest(
        `/history/user/single/${userId}/`,
        `status=${this.statusString}`,
      )
        .then((response) => {
          const datasets = [];
          // eslint-disable-next-line no-restricted-syntax, guard-for-in
          for (const key in response) {
            const rnd = generateRandomNumbers();
            const color = `rgba(${rnd[0]},${rnd[1]},${rnd[2]}, 1)`;
            const obj = {
              label: key,
              backgroundColor: color,
              borderColor: color,
              data: [response[key]],
            };
            datasets.push(obj);
          }
          this.totalActivity.type = "bar";
          this.totalActivity.data = { labels: ["Total Activity"], datasets };
          this.totalActivity.loading = false;
        })
        .catch(() => {
          this.totalActivity = {
            loading: false,
            type: "",
            data: {},
          };
          this.noActivity = true;
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 2000);
        });
    },
    getPast24hUserActivity(userId) {
      this.past24hActivity.loading = true;
      getRequest(
        `/history/user/single/${userId}/`,
        `recent=true&details=false&status=${this.statusString}`,
      )
        .then((response) => {
          const datasets = [];
          // eslint-disable-next-line no-restricted-syntax, guard-for-in
          for (const key in response) {
            const rnd = generateRandomNumbers();
            const color = `rgba(${rnd[0]},${rnd[1]},${rnd[2]}, 1)`;
            const obj = {
              label: key,
              backgroundColor: color,
              borderColor: color,
              data: [response[key]],
            };
            datasets.push(obj);
          }

          this.past24hActivity.type = "bar";
          this.past24hActivity.data = {
            labels: ["Past 24h Activity"],
            datasets,
          };
          this.past24hActivity.loading = false;
        })
        .catch(() => {
          this.past24hActivity = {
            loading: false,
            type: "",
            data: {},
          };
          this.noActivity = true;
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 2000);
        });
    },
    getPastUserActivity(userId) {
      this.pastActivity.loading = true;
      getRequest(
        `/history/user/single/${userId}/`,
        `recent=true&details=true&period=${
          this.pastActivity.timePeriod
        }&time=${this.pastActivity.time}&status=${this.statusString}`,
      )
        .then((response) => {
          // LABELS
          const labels = [];
          response[Object.keys(response)[0]].forEach((element) => {
            if (this.pastActivity.timePeriod === "Day") {
              labels.push(
                `${moment(element.startingTime).format(
                  "LT",
                )} - ${moment(element.endingTime).format("LT")}`,
              );
            } else if (this.pastActivity.timePeriod === "Week") {
              labels.push(
                `${moment(element.startingTime).format("ddd, MMM D")}`,
              );
            } else if (this.pastActivity.timePeriod === "Month") {
              labels.push(`${moment(element.startingTime).format("D")}`);
            }
          });
          // DATASETS
          const datasets = [];
          // eslint-disable-next-line no-restricted-syntax, guard-for-in
          for (const key in response) {
            const rnd = generateRandomNumbers();
            const data = [];
            response[key].forEach((element) => {
              data.push(element.value);
            });
            const obj = {
              label: key,
              // For bar charts a near-transparent fill makes bars look "missing"
              backgroundColor: `rgba(${rnd[0]},${rnd[1]},${rnd[2]}, 0.45)`,
              borderColor: `rgba(${rnd[0]},${rnd[1]},${rnd[2]})`,
              borderWidth: 1,
              data,
            };
            datasets.push(obj);
          }
          this.pastActivity.type = "bar";
          this.pastActivity.data = { labels, datasets };
          this.pastActivity.loading = false;
        })
        .catch(() => {
          this.pastActivity = {
            loading: false,
            type: "",
            data: {},
          };
          this.noActivity = true;
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 2000);
        });
    },
  },
};
</script>
